/**
 * List-row card for a course with progress and continue actions.
 */
import React from "react";
import { Icon } from "@iconify/react";
import { Box } from "@mui/material";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import { VButton, VText } from "form";
import { calcProgressValue } from "utils/grade.util";
import { getCourseImageUrl } from "utils/string";
import BThumb from "./BThumb";

/**
 * Horizontal course row used in assigned and dashboard lists.
 *
 * @param {{ course: Object, isProgress?: boolean }} props
 */
const BCourseListItem = (props) => {
  const { course, isProgress = true } = props;

  const { t } = useTranslation("common");
  const navigate = useNavigate();

  /**
   * Navigates to the course overview.
   *
   * @param {string} courseId Course identifier.
   * @returns {void}
   */
  const onClick = (courseId) => navigate(`/course/${courseId}`);
  /**
   * Navigates to the course guide.
   *
   * @param {string} courseId Course identifier.
   * @returns {void}
   */
  const onGuideClick = (courseId) => navigate(`/guide/${courseId}`);

  return (
    <Box className="flex flex-row justify-between rounded border border-solid border-gray-200 dark:border-gray-500 pr-4">
      <Box className="flex flex-row gap-4 items-center">
        <BThumb src={getCourseImageUrl(course.course_image_url)} className="aspect-[9/5] rounded-md h-32 w-56 flex-shrink-0" />
        <Box className="py-4 flex flex-col w-full gap-0.5">
          <VText className="text-xl text-limit-1">{course.display_name || "-"}</VText>

          {course.enrollment_status && (
            <>
              <VText color="secondary" className="flex items-center text-sm text-limit-1">
                <Icon icon="lucide:clock-9" className="flex-shrink-0" />
                &nbsp;
                {course.last_learned ? `${t("Last learned")} ${Math.round((new Date() - course.last_learned) / (1000 * 60))} min ${t("ago")}` : t("You did not study after login!")}
              </VText>
              <VButton color="secondary" className="flex justify-start items-center text-sm px-0" onClick={() => onGuideClick(course.id)}>
                <Icon icon="lucide:file-search" />
                &nbsp;
                <VText color="custom" className="text-red-500">
                  {t("View Guide")}
                </VText>
              </VButton>
            </>
          )}
        </Box>
      </Box>
      <Box className="flex flex-col justify-center w-3/12">
        {isProgress && (
          <Box className="w-full bg-gray-200 h-1.5 rounded-full">
            <Box className="bg-green-600 h-1.5 rounded-full" sx={{ width: `${calcProgressValue(course)}%` }} />
          </Box>
        )}

        {course.enrollment_status ? (
          <VButton variant="contained" color="primary" className="w-full normal-case mt-4" onClick={() => onClick(course.id)}>
            {t("Continue")}
          </VButton>
        ) : (
          <Link to="/inbox" className="no-underline">
            <VButton variant="outlined" color="primary" className="w-full normal-case mt-4" onClick={() => onClick(course.id)}>
              {t("Require Assign")}
            </VButton>
          </Link>
        )}
      </Box>
    </Box>
  );
};

BCourseListItem.propTypes = {
  course: PropTypes.shape({
    id: PropTypes.string.isRequired,
    display_name: PropTypes.string.isRequired,
    enrollment_status: PropTypes.bool.isRequired,
    last_learned: PropTypes.instanceOf(Date),
    course_image_url: PropTypes.string.isRequired,
  }).isRequired,
  isProgress: PropTypes.bool.isRequired,
};

export default BCourseListItem;
